'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import styles from './AttachmentTray.module.css';
import { AttachmentChip, type AttachmentChipProps } from './AttachmentChip';
import { Alert } from './Alert';

export interface AttachmentTrayItem extends Omit<AttachmentChipProps, 'onRemove'> {
  /** Stable id used as key + passed to onRemove. */
  id: string;
}

export interface AttachmentTrayError {
  id?: string;
  filename: string;
  reason: string;
}

export interface AttachmentTrayProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Pending/uploaded/errored attachments to render as chips. */
  items: AttachmentTrayItem[];
  /** Validation errors to surface as dismissible alerts. */
  errors?: AttachmentTrayError[];
  onRemove?: (id: string) => void;
  onDismissError?: (index: number) => void;
  className?: string;
  /** Hide the bottom border (useful when nesting inside another bordered panel). */
  hideBorder?: boolean;
}

/**
 * AttachmentTray
 *
 * Shows pending file chips (rendered with `AttachmentChip`) and inline
 * validation errors (rendered with the `Alert` primitive). Returns
 * `null` when there's nothing to show so layout doesn't shift.
 */
export const AttachmentTray = React.forwardRef<HTMLDivElement, AttachmentTrayProps>(
  ({ items, errors = [], onRemove, onDismissError, className, hideBorder, ...rest }, ref) => {
    if (items.length === 0 && errors.length === 0) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={clsx(styles.tray, className)}
        style={hideBorder ? { borderBottom: 'none' } : undefined}
        {...rest}
      >
        {items.length > 0 && (
          <ul role="list" aria-label="Attached files" className={styles.list}>
            {items.map((item) => (
              <AttachmentChip
                key={item.id}
                filename={item.filename}
                mediaType={item.mediaType}
                size={item.size}
                status={item.status}
                thumbnailUrl={item.thumbnailUrl}
                error={item.error}
                metaText={item.metaText}
                icon={item.icon}
                ariaLabel={item.ariaLabel}
                onRemove={onRemove ? () => onRemove(item.id) : undefined}
              />
            ))}
          </ul>
        )}

        {errors.length > 0 && (
          <ul aria-live="polite" className={styles.errorList}>
            {errors.map((err, idx) => (
              <li key={err.id ?? `${err.filename}-${idx}`} className={styles.errorItem}>
                <Alert
                  variant="error"
                  dismissible={!!onDismissError}
                  onDismiss={() => onDismissError?.(idx)}
                >
                  <strong>{err.filename}</strong> — {err.reason}
                </Alert>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
);

AttachmentTray.displayName = 'AttachmentTray';

export default AttachmentTray;
